import { createEffect, createSignal, For, Show } from "solid-js";
import { createStore, produce } from "solid-js/store";
import { msg } from "@lingui/core/macro";
import { Plus, Save, Trash2 } from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { QueryErrorState } from "@/components/ui/query-error-state";
import type { MailRule, RuleActionType, RuleField, RuleOp } from "../api";
import { useMailRules, useSaveRules } from "../hooks";
import { Field, MailInput } from "./field";

const FIELD_OPTS: RuleField[] = ["from", "to", "subject", "body"];
const OP_OPTS: RuleOp[] = ["contains", "equals", "starts_with", "ends_with"];
const ACTION_OPTS: RuleActionType[] = ["move", "add_label", "add_flag", "mark_read", "delete"];

function emptyRule(): MailRule {
  return {
    name: "",
    matchKind: "all",
    conditions: [{ field: "from", op: "contains", value: "" }],
    actions: [{ type: "mark_read" }],
    stop: false,
  };
}

export function RulesEditor(props: { accountId: string }) {
  const { t } = useI18n();
  const rulesQuery = useMailRules(() => props.accountId);
  const saveMutation = useSaveRules();

  const [rules, setRules] = createStore<MailRule[]>([]);
  const [status, setStatus] = createSignal<string | null>(null);

  // Seed the editable store when the server rules load / change account.
  // NB: `rulesQuery.data` is a fine-grained Solid store proxy (TanStack Solid
  // Query), and `structuredClone` throws "could not be cloned" on a proxy — it
  // only stayed latent while the list was empty. Rebuilding each rule with
  // spreads deep-copies and strips the proxy (no `any`/JSON round-trip),
  // giving the editable store plain objects while keeping the `MailRule[]` type.
  createEffect(() => {
    // P23 / CC-60 · I2: `.data` only on `isSuccess`.
    const data = rulesQuery.isSuccess ? rulesQuery.data : undefined;
    if (data) {
      setRules(
        data.map((r) => ({
          ...r,
          conditions: r.conditions.map((c) => ({ ...c })),
          actions: r.actions.map((a) => ({ ...a })),
        })),
      );
    }
  });

  /** Mutate rule at index `ri` behind a guard (satisfies noUncheckedIndexedAccess). */
  const editRule = (ri: number, fn: (rule: MailRule) => void) => {
    setRules(
      produce((rs) => {
        const rule = rs[ri];
        if (rule) {
          fn(rule);
        }
      }),
    );
  };

  const save = () => {
    setStatus(null);
    saveMutation.mutate(
      { accountId: props.accountId, rules: [...rules] },
      {
        onSuccess: () => setStatus(t(msg`Rules saved.`)),
        onError: (err) => setStatus(err.message),
      },
    );
  };

  return (
    <div class="flex flex-col gap-4" data-testid="rules-editor">
      {/* P23 / CC-60 · I3: with the API down the editor started EMPTY and a
          "Save" would have deleted real rules on the server. The error is
          WRAPPED (on error `isLoading` is `false`). */}
      <Show when={rulesQuery.isError}>
        <QueryErrorState
          testId="mail-rules-error"
          compact
          title={t(msg`Couldn't load your filter rules`)}
          description={t(
            msg`We couldn't reach the mail server. Editing now could overwrite the rules you already have.`,
          )}
          onRetry={() => void rulesQuery.refetch()}
        />
      </Show>
      <Show when={!rulesQuery.isError}>
        <Show
          when={!rulesQuery.isLoading}
          fallback={<p class="text-sm text-muted-foreground">{t(msg`Loading…`)}</p>}
        >
          <For each={rules}>
            {(rule, ri) => (
              <div
                class="rounded-lg border border-border/50 bg-card/30 p-4"
                data-testid="rule-card"
              >
                <div class="flex items-end gap-2">
                  <div class="flex-1">
                    <Field label={t(msg`Rule name`)}>
                      <MailInput
                        value={rule.name}
                        onInput={(e) => {
                          const v = e.currentTarget.value;
                          editRule(ri(), (r) => (r.name = v));
                        }}
                        placeholder={t(msg`e.g. Newsletters`)}
                        data-testid="rule-name"
                      />
                    </Field>
                  </div>
                  <div class="w-40">
                    <Field label={t(msg`Match`)}>
                      <Select<"all" | "any">
                        value={rule.matchKind}
                        onChange={(v) => editRule(ri(), (r) => (r.matchKind = v))}
                        options={[
                          { value: "all", label: t(msg`All conditions`) },
                          { value: "any", label: t(msg`Any condition`) },
                        ]}
                      />
                    </Field>
                  </div>
                  <button
                    type="button"
                    class="mb-1 flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => setRules(produce((rs) => rs.splice(ri(), 1)))}
                    aria-label={t(msg`Delete rule`)}
                    data-testid="rule-delete"
                  >
                    <Trash2 class="h-4 w-4" />
                  </button>
                </div>

                {/* Conditions */}
                <p class="mt-3 mb-1.5 text-xs font-medium text-muted-foreground">
                  {t(msg`Conditions`)}
                </p>
                <For each={rule.conditions}>
                  {(cond, ci) => (
                    <div class="mb-1.5 grid grid-cols-[8rem_9rem_1fr_auto] gap-2">
                      <Select<RuleField>
                        aria-label={t(msg`Field`)}
                        value={cond.field}
                        onChange={(v) => {
                          const ci_ = ci();
                          editRule(ri(), (r) => {
                            const c = r.conditions[ci_];
                            if (c) {
                              c.field = v;
                            }
                          });
                        }}
                        options={FIELD_OPTS.map((f) => ({ value: f, label: f }))}
                      />
                      <Select<RuleOp>
                        aria-label={t(msg`Operator`)}
                        value={cond.op}
                        onChange={(v) => {
                          const ci_ = ci();
                          editRule(ri(), (r) => {
                            const c = r.conditions[ci_];
                            if (c) {
                              c.op = v;
                            }
                          });
                        }}
                        options={OP_OPTS.map((o) => ({ value: o, label: o.replace("_", " ") }))}
                      />
                      <MailInput
                        value={cond.value}
                        onInput={(e) => {
                          const v = e.currentTarget.value;
                          const ci_ = ci();
                          editRule(ri(), (r) => {
                            const c = r.conditions[ci_];
                            if (c) {
                              c.value = v;
                            }
                          });
                        }}
                        placeholder={t(msg`value`)}
                        data-testid="rule-condition-value"
                      />
                      <button
                        type="button"
                        class="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => {
                          const ci_ = ci();
                          editRule(ri(), (r) => r.conditions.splice(ci_, 1));
                        }}
                        aria-label={t(msg`Remove condition`)}
                      >
                        <Trash2 class="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </For>
                <Button
                  variant="ghost"
                  size="sm"
                  class="gap-1"
                  onClick={() =>
                    editRule(ri(), (r) =>
                      r.conditions.push({ field: "from", op: "contains", value: "" }),
                    )
                  }
                >
                  <Plus class="h-3.5 w-3.5" /> {t(msg`Add condition`)}
                </Button>

                {/* Actions*/}
                <p class="mt-3 mb-1.5 text-xs font-medium text-muted-foreground">
                  {t(msg`Actions`)}
                </p>
                <For each={rule.actions}>
                  {(action, ai) => (
                    <div class="mb-1.5 grid grid-cols-[10rem_1fr_auto] gap-2">
                      <Select<RuleActionType>
                        aria-label={t(msg`Action`)}
                        value={action.type}
                        onChange={(v) => {
                          const ai_ = ai();
                          editRule(ri(), (r) => {
                            const a = r.actions[ai_];
                            if (a) {
                              a.type = v;
                            }
                          });
                        }}
                        options={ACTION_OPTS.map((a) => ({ value: a, label: a.replace("_", " ") }))}
                      />
                      <MailInput
                        value={action.value ?? ""}
                        onInput={(e) => {
                          const v = e.currentTarget.value;
                          const ai_ = ai();
                          editRule(ri(), (r) => {
                            const a = r.actions[ai_];
                            if (a) {
                              a.value = v;
                            }
                          });
                        }}
                        placeholder={t(msg`value (folder / label / flag)`)}
                        data-testid="rule-action-value"
                      />
                      <button
                        type="button"
                        class="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => {
                          const ai_ = ai();
                          editRule(ri(), (r) => r.actions.splice(ai_, 1));
                        }}
                        aria-label={t(msg`Remove action`)}
                      >
                        <Trash2 class="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </For>
                <Button
                  variant="ghost"
                  size="sm"
                  class="gap-1"
                  onClick={() => editRule(ri(), (r) => r.actions.push({ type: "mark_read" }))}
                >
                  <Plus class="h-3.5 w-3.5" /> {t(msg`Add action`)}
                </Button>

                <label class="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={rule.stop}
                    onChange={(e) => {
                      const { checked } = e.currentTarget;
                      editRule(ri(), (r) => (r.stop = checked));
                    }}
                    data-testid="rule-stop"
                  />
                  {t(msg`Stop processing further rules after this one`)}
                </label>
              </div>
            )}
          </For>

          <div class="flex items-center gap-2">
            <Button
              variant="outline"
              class="gap-1.5"
              onClick={() => setRules(produce((rs) => rs.push(emptyRule())))}
              data-testid="rule-add"
            >
              <Plus class="h-4 w-4" /> {t(msg`Add rule`)}
            </Button>
            <Button
              class="gap-1.5"
              onClick={save}
              disabled={saveMutation.isPending}
              data-testid="rules-save"
            >
              <Save class="h-4 w-4" />
              {saveMutation.isPending ? t(msg`Saving…`) : t(msg`Save rules`)}
            </Button>
          </div>

          <Show when={status()}>
            <p class="text-sm text-muted-foreground" role="status" data-testid="rules-status">
              {status()}
            </p>
          </Show>
        </Show>
      </Show>
    </div>
  );
}
